import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  /** Additional header decoration (badges, scope label, ...). */
  headerExtras?: React.ReactNode;
  /** Sticky footer content (typically Cancel / Confirm buttons). */
  footer?: React.ReactNode;
  /** Width preset. Default "lg". */
  size?: "md" | "lg" | "xl";
  children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  headerExtras,
  footer,
  size = "lg",
  children,
}: ModalProps) {
  // Close on Esc; lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="modal-root" role="dialog" aria-modal="true">
      <div className="modal-backdrop" onClick={onClose} />
      <div className={`modal modal--${size}`}>
        <header className="modal__head">
          <div>
            <div className="modal__title">{title}</div>
            {subtitle && <div className="modal__subtitle">{subtitle}</div>}
          </div>
          <div className="modal__head-extras">
            {headerExtras}
            <button
              type="button"
              className="btn btn--ghost btn--icon"
              aria-label="close"
              onClick={onClose}
            >
              <X />
            </button>
          </div>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
